import { useMatches, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ResolvedBreadcrumbItem } from '@/components/navigation/ResolvedBreadcrumbItem'
import { useDraftStore } from '@/stores/draftStore'
import { useTabsStore } from '@/stores/tabsStore'

interface BreadcrumbMatch {
  handle?: {
    crumb?: string | ((match: { params: Record<string, string | undefined> }) => string | null | undefined)
  }
  params: Record<string, string | undefined>
  pathname: string
}

function Separator() {
  return (
    <span
      aria-hidden
      style={{ fontSize: 12, color: 'var(--text-dim, #4b5563)', userSelect: 'none', flexShrink: 0 }}
    >
      /
    </span>
  )
}

export function AppBreadcrumb() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentProjectName = useDraftStore((s) => s.currentProjectName)
  const activeTabName = useTabsStore((s) => {
    const activeTab = s.tabs.find((tab) => tab.id === s.activeTabId)
    return activeTab?.remoteName ?? activeTab?.title ?? null
  })
  const matches = useMatches() as BreadcrumbMatch[]
  const isTranslationKey = (value: string) =>
    value.startsWith('navigation.') ||
    value.startsWith('workspace.') ||
    value.startsWith('workspaces.') ||
    value.startsWith('billing.')
  const crumbs = matches
    .map((match) => {
      const crumb = match.handle?.crumb
      if (!crumb) return null
      const rawLabel = typeof crumb === 'function' ? crumb({ params: match.params }) : crumb
      if (!rawLabel) return null
      let label = isTranslationKey(rawLabel) ? t(rawLabel, rawLabel) : rawLabel
      if (rawLabel === 'navigation.breadcrumbs.editor') {
        label = activeTabName ?? currentProjectName ?? label
      }
      if (!label) return null
      return { label, params: match.params, pathname: match.pathname }
    })
    .filter((item): item is { label: string; params: Record<string, string | undefined>; pathname: string } => item !== null)

  if (!crumbs.length) return null

  return (
    <nav
      aria-label={t('navigation.currentPageAria')}
      style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexShrink: 1, overflow: 'hidden' }}
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1
        return (
          <div key={`${crumb.pathname}-${crumb.label}`} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {index > 0 ? <Separator /> : null}
            <ResolvedBreadcrumbItem
              isLast={isLast}
              label={crumb.label}
              onNavigate={navigate}
              params={crumb.params}
              pathname={crumb.pathname}
            />
          </div>
        )
      })}
    </nav>
  )
}
